import type { Ticker } from '@cutr/constants/cutlist-theme.ts';
import { WINDOW_ORDER_STATUS } from '@cutr/constants/windows/windows';
import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuthStore } from '@/api/login';
import { ConfirmationDialog } from '@/blocks/ConfirmationDialog';
import { Button, RouterButton } from '@/primitives/Button';
import { ArrowLeft, Icon, Logout, Trash } from '@/primitives/Icons';
import { getSourceConfig, useTicker } from '@/theme';
import { useWindowStore } from '@/windows/api/windowStore';
import { agentApi } from '@/windows/queries/agent/api';
import { useCancelOrder } from '@/windows/queries/agent/queries';

export const WindowsAgentHeader = () => {
  const { t } = useTranslation();
  const config = getSourceConfig();
  const Logo = config.logo;
  const { orderId } = useParams();

  return (
    <>
      <header
        style={{ boxShadow: 'none', borderBottom: '1px solid var(--primary)' }}
      >
        <a href={'/admin/windows/orders'} className="logo">
          <Logo />
        </a>
        <div
          className="flexAlign"
          style={{
            width: '100%',
            justifyContent: 'space-between',
            marginLeft: '3rem',
          }}
        >
          <div style={{ marginLeft: 'auto' }} className="flexAlign">
            {orderId && <CancelOrderBtn orderId={orderId} />}
            {orderId && (
              <RouterButton
                variant="header"
                icon={<Icon icon={<ArrowLeft />} />}
                to="/admin/windows/orders"
              >
                {t('agent.quoteSummary.backToOrders')}
              </RouterButton>
            )}
            <LogoutBtn />
            <Suspense fallback={'...'}>
              <LanguageSelector />
            </Suspense>
          </div>
        </div>
      </header>
    </>
  );
};

const LanguageSelector = () => {
  const { t, i18n } = useTranslation();
  const theme = useTicker();
  const currentLng = i18n.resolvedLanguage;

  const languagesForThemes = (theme: Ticker) => {
    if (theme === 'SWISSKRONO') return ['en', 'de'];
    if (theme === 'CUTR') return ['en', 'de', 'nl'];
    if (theme === 'SORPETALER') return ['en', 'de'];

    return ['en', 'nl'];
  };

  const changeLanguage = async (lng: string) => {
    i18n.changeLanguage(lng);
    await agentApi.updateAgentLocale();
  };

  return (
    <div className="flexAlign gap-xxs">
      {languagesForThemes(theme).map((lng) => {
        const style =
          lng === currentLng
            ? {
                fontWeight: 'bold',
                textDecoration: 'underline',
              }
            : undefined;
        return (
          <Button
            key={lng}
            variant="iconHeader"
            onClick={async () => await changeLanguage(lng)}
            style={style}
          >
            {t(`common.lng.${lng}`)}
          </Button>
        );
      })}
    </div>
  );
};

const CancelOrderBtn = ({ orderId }: { orderId: string }) => {
  const { t } = useTranslation();
  const { status } = useWindowStore();
  const { mutateAsync: cancelOrder, isPending } = useCancelOrder(orderId);
  const [isOpen, setIsOpen] = React.useState(false);

  const isCancellable =
    status !== WINDOW_ORDER_STATUS.cancelled &&
    status !== WINDOW_ORDER_STATUS.order_placed;

  if (!isCancellable) return null;

  const handleConfirm = async () => {
    setIsOpen(false);
    try {
      await cancelOrder();
      toast.success(t('agent.quoteSummary.cancelOrderSuccess'));
    } catch (error) {
      toast.error(
        t('agent.quoteSummary.cancelOrderError', {
          error: (error as Error)?.message,
        })
      );
    }
  };

  return (
    <>
      <Button
        variant="header"
        style={{
          border: '1px solid var(--red-5)',
          color: 'var(--red-5)',
        }}
        icon={<Icon icon={<Trash />} />}
        onClick={() => setIsOpen(true)}
        disabled={isPending}
      >
        {t('agent.quoteSummary.cancelOrder')}
      </Button>
      <ConfirmationDialog
        isOpen={isOpen}
        title={t('agent.quoteSummary.cancelOrder')}
        onConfirm={handleConfirm}
        onClose={() => setIsOpen(false)}
        variant="alert"
      >
        {t('agent.quoteSummary.cancelOrderConfirm')}
      </ConfirmationDialog>
    </>
  );
};

const LogoutBtn = () => {
  const { logout } = useAuthStore();
  const { t } = useTranslation();
  const { agent } = useAuthStore();

  const onLogout = () => {
    logout();
  };

  return (
    <Button
      variant="header"
      icon={<Icon icon={<Logout />} />}
      onClick={onLogout}
      className="tooltipBottom"
      tooltip={agent?.email}
    >
      {t('common.logout')}
    </Button>
  );
};
